import { ButtonDropdown, ButtonDropdownProps, NavItem } from '@billy/design-system'
import { differenceInYears, endOfYear, setYear } from 'date-fns'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { PeriodValue } from '../../../../types/periodValue'
import { PeriodComparisonValue } from '../../types/periodComparisonValue'
import { getItems } from './utils/getItems'

const MAX_PERIODS_DEFAULT = 12

export interface PeriodsComparisonDropdownProps extends Omit<ButtonDropdownProps<number>, 'items'> {
  mode: PeriodComparisonValue
  period?: PeriodValue
}

export const PeriodsComparisonDropdown = ({
  mode,
  period,
  selectedId: selectedIdControlled,
  onSelect,
}: PeriodsComparisonDropdownProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const [selectedId, setSelectedId] = useState(selectedIdControlled)

  const maxPeriods = useMemo(() => {
    if (mode !== 'year' || !organization?.firstFiscalYearStart || !period) {
      return MAX_PERIODS_DEFAULT
    }

    const dateToCompare = period.year !== undefined ? setYear(new Date(), period.year) : new Date()
    const firstFiscalYearStart = new Date(organization.firstFiscalYearStart)
    const difference = differenceInYears(endOfYear(dateToCompare), endOfYear(firstFiscalYearStart))
    return difference >= 0 ? difference : MAX_PERIODS_DEFAULT
  }, [mode, organization?.firstFiscalYearStart, period])

  const items: NavItem<number>[] = useMemo(() => getItems(maxPeriods, mode, t), [maxPeriods, mode, t])

  useEffect(() => {
    const isSelectedItemAvailable = !!items?.find((item) => item.id === selectedId)

    if (!isSelectedItemAvailable && items?.length) {
      const lastItem = items[items.length - 1]
      setSelectedId(lastItem.id)
      onSelect?.(lastItem.id, lastItem.value)
    }
  }, [items, onSelect, selectedId])

  const getDisplayValue = useCallback(
    (item: NavItem<number>) => {
      if (item.value === 0) {
        return item.children
      }
      return t('reports.compare_with', { count: item.value })
    },
    [t],
  )

  if (period?.mode === 'all') {
    return <></>
  }

  return (
    <ButtonDropdown
      items={items}
      onSelect={onSelect}
      placement="bottom-start"
      selectedId={selectedId}
      variant="outlined"
      getDisplayValue={getDisplayValue}
    />
  )
}
